import { PrettyProviderName } from "@logic/lib/loans"

import type { LightReachPricing } from "~/api/dto/light-reach.dto"
import type { PowurOsPricingDto } from "~/api/dto/proposal.dto"
import type { LoanProduct } from "~/graphql/client"
import { LoanProvider } from "~/graphql/client"
import { FinanceType } from "~/utils/finance/constants"
import { formatDecimal } from "~/utils/finance/format"
import { isDefined, maybeHas } from "~/utils/predicates"

export const loanProductsToOptions = (
  loanProducts: LoanProduct[] = [],
  type = `Cash`,
): string[] => {
  return loanProducts
    .filter((item) => item?.provider === type)
    .map(makeOptionName)
    .filter(isDefined)
    .filter(maybeHas)
}

export const orderLoanTypes = (
  providerTypes: string[] = [],
  _env: string,
): string[] => {
  const baseTypes = [
    FinanceType.CASH,
    FinanceType.CONCERT,
    FinanceType.LIGHT_REACH,
    ...providerTypes,
  ]

  return [...new Set(baseTypes)]
}

export const shouldSkipFinanceProductStep = (financeType: string): boolean => {
  return financeType === FinanceType.CASH
}

export const shouldResetNewCustomerIncentive = (
  financeType: string,
): boolean => {
  return (
    financeType === FinanceType.LIGHT_REACH ||
    financeType === FinanceType.GOODLEAP ||
    financeType === FinanceType.CONCERT
  )
}

// Concert requires battery storage only for PG&E in California.
const CONCERT_CA_BATTERY_REQUIRED_LSE_ID = 734

export const requiresBatteryForConcertCa = ({
  financeType,
  lseId,
  state,
}: {
  financeType: string | null | undefined
  lseId: number | null | undefined
  state: string | null | undefined
}): boolean => {
  return (
    financeType === FinanceType.CONCERT &&
    state?.toLowerCase() === `ca` &&
    lseId === CONCERT_CA_BATTERY_REQUIRED_LSE_ID
  )
}

export function byTermThenApr(a: LoanProduct, b: LoanProduct): number {
  const isGoodleapOrSungage = (provider: string) => {
    return (
      provider === LoanProvider.goodleap || provider === LoanProvider.sungage
    )
  }
  const aTerm = isGoodleapOrSungage(a.provider) ? a.term * 12 : a.term
  const bTerm = isGoodleapOrSungage(b.provider) ? b.term * 12 : b.term
  const termDif = aTerm - bTerm
  const tie = termDif === 0

  if (tie) {
    const rateDif = a.rate - b.rate

    return rateDif
  }

  return termDif
}

// Default Display Name = `${Lender} ${Term} Year ${APR}%`
// if goodleap and has extra product name add that
export function makeOptionName(
  item: Pick<
    LoanProduct,
    | `escalator`
    | `loanType`
    | `provider`
    | `providerMeta`
    | `providerName`
    | `rate`
    | `term`
  > | null,
): string {
  if (!item) {
    return ``
  }
  const {
    provider,
    term,
    rate,
    providerMeta,
    providerName,
    loanType,
    escalator,
  } = item
  let extra = ``
  const isGoodleap = provider === `goodleap`
  const isMosaic = provider === `mosaic`
  const isEnfin = provider === `enfin`
  const isSungage = provider === `sungage`
  const isConcert = provider === `concert`
  const isCreditHuman = provider === `credit_human`
  const isLightReach = provider === `lightreach`
  const isPPA = loanType === `ppa`
  if (isGoodleap) {
    if (isPPA) {
      extra = ` - ${providerName}`
    } else {
      if (providerMeta.productCode !== `STANDARD_SOLAR`) {
        extra = ` - ${providerMeta.productName}`
      }
    }
    if (isPPA && term) {
      return `${PrettyProviderName[provider]} ${extra}`
    }
  }
  if (isMosaic && providerMeta.financingProductType === `Zero`) {
    extra = ` - PowerSwitch Zero 7.0`
  }

  if (isSungage) {
    const dppProduct = getSungageDppProduct(loanType, providerMeta)
    extra =
      dppProduct === `Escalator`
        ? ` - Sunrise Escalator at ${formatDecimal(escalator)}%`
        : ` - BrightStar`
  }

  if (isLightReach) {
    return `${providerName}`
  }

  if (provider && term) {
    return `${PrettyProviderName[provider]} ${
      isGoodleap || isSungage || isEnfin || isConcert || isCreditHuman
        ? term
        : term / 12
    } Year ${rate}%${extra}`
  }

  return `this loan`
}

const getSungageDppProduct = (
  loanType: string,
  providerMeta: LoanProduct[`providerMeta`],
): `Classic DPP` | `Escalator` => {
  const productFromMeta =
    typeof providerMeta === `object` &&
    providerMeta !== null &&
    `dppProduct` in providerMeta
      ? String(providerMeta.dppProduct ?? ``).toLowerCase()
      : ``

  if (productFromMeta.includes(`escalator`) || loanType === `escalator`) {
    return `Escalator`
  }

  return `Classic DPP`
}

export const getGoodleapLoanTypeFromGroup = (
  group: string | null,
): `lease` | `ppa` | null => {
  if (!group) {
    return null
  }

  const normalizedGroup = group.toLowerCase()

  if (normalizedGroup.includes(`tpo`)) {
    return `ppa`
  }

  if (normalizedGroup.includes(`loan`)) {
    return `lease`
  }

  return null
}

export const getSungageLoanTypeFromGroup = (
  group: string | null,
): `escalator` | `lease` | null => {
  if (!group) {
    return null
  }

  const normalizedGroup = group.toLowerCase()

  if (
    normalizedGroup.includes(`sunrise`) ||
    normalizedGroup.includes(`escalator`)
  ) {
    return `escalator`
  }

  if (normalizedGroup.includes(`bright`)) {
    return `lease`
  }

  return null
}
export const PRICE_TOLERANCE = 0.1

export enum PriceMatchType {
  EXACT = `exact`,
  ROUNDED_UP = `rounded_up`,
  ROUNDED_DOWN = `rounded_down`,
  SELECTED = `selected`,
}

export type LightReachPricingWithMatch = LightReachPricing & {
  matchType: PriceMatchType
  allOptions: LightReachPricing[]
}

export function filterAvailableProducts(
  data: Map<string, LightReachPricing[]>,
  pricePerWatt: number,
  selectedProductId?: string | null,
  bounds?: { min: number; max: number },
): LightReachPricingWithMatch[] {
  return Object.entries(data)
    .map(([, pricingArray]: [string, LightReachPricing[]]) => {
      const boundedPricingArray = bounds
        ? pricingArray.filter(
            (pricing: LightReachPricing) =>
              pricing.ppwRate >= bounds.min && pricing.ppwRate <= bounds.max,
          )
        : pricingArray

      if (boundedPricingArray.length === 0) {
        return null
      }

      // Sort allOptions by ppwRate for consistent display
      const allOptions = [...boundedPricingArray].sort(
        (a, b) => a.ppwRate - b.ppwRate,
      )

      // Step 1: Try to find exact match (with floating-point tolerance)
      const exactMatch = boundedPricingArray.find(
        (pricing: LightReachPricing) =>
          Math.abs(pricing.ppwRate - pricePerWatt) < 0.001,
      )

      if (exactMatch) {
        return {
          ...exactMatch,
          matchType: PriceMatchType.EXACT,
          allOptions,
        }
      }

      // Step 2: Find closest match rounded up within tolerance
      const validOptions = boundedPricingArray.filter(
        (pricing: LightReachPricing) => {
          const difference = pricing.ppwRate - pricePerWatt
          return difference > 0 && difference <= PRICE_TOLERANCE
        },
      )

      if (validOptions.length > 0) {
        // Return the closest (lowest) option that's still higher than target
        const closest = validOptions.reduce(
          (closest: LightReachPricing, current: LightReachPricing) =>
            current.ppwRate < closest.ppwRate ? current : closest,
        )

        return {
          ...closest,
          matchType: PriceMatchType.ROUNDED_UP,
          allOptions,
        }
      }

      // Step 3: Find closest match rounded down within tolerance
      const lowerOptions = boundedPricingArray.filter(
        (pricing: LightReachPricing) => {
          const difference = pricePerWatt - pricing.ppwRate
          return difference > 0 && difference <= PRICE_TOLERANCE
        },
      )

      if (lowerOptions.length > 0) {
        // Return the closest (highest) option that's still lower than target
        const closest = lowerOptions.reduce(
          (closest: LightReachPricing, current: LightReachPricing) =>
            current.ppwRate > closest.ppwRate ? current : closest,
        )

        return {
          ...closest,
          matchType: PriceMatchType.ROUNDED_DOWN,
          allOptions,
        }
      }

      // Step 4: If no match within tolerance, use closest in-bounds option.
      // Tie-breaker: choose higher ppwRate.
      if (bounds) {
        const closestInBounds = boundedPricingArray.reduce(
          (closest: LightReachPricing, current: LightReachPricing) => {
            const closestDifference = Math.abs(closest.ppwRate - pricePerWatt)
            const currentDifference = Math.abs(current.ppwRate - pricePerWatt)

            if (currentDifference < closestDifference) {
              return current
            }

            if (currentDifference === closestDifference) {
              return current.ppwRate > closest.ppwRate ? current : closest
            }

            return closest
          },
        )

        if (closestInBounds) {
          const matchType =
            closestInBounds.ppwRate >= pricePerWatt
              ? PriceMatchType.ROUNDED_UP
              : PriceMatchType.ROUNDED_DOWN

          return {
            ...closestInBounds,
            matchType,
            allOptions,
          }
        }
      }

      // Step 5: If no match within tolerance, check if selected product is in this group
      if (selectedProductId) {
        const selectedProduct = boundedPricingArray.find(
          (pricing: LightReachPricing) =>
            pricing.productId === selectedProductId,
        )

        if (selectedProduct) {
          return {
            ...selectedProduct,
            matchType: PriceMatchType.SELECTED,
            allOptions,
          }
        }
      }

      return null
    })
    .filter((pricing): pricing is LightReachPricingWithMatch => pricing !== null)
}

export const getSolarOnlyPpwBoundsFromComparisons = (
  comparisonPricing: PowurOsPricingDto[] | undefined,
  batteryCost: number,
): {
  min: number | null
  max: number | null
} => {
  if (!comparisonPricing || comparisonPricing.length === 0) {
    return { min: null, max: null }
  }

  const solarOnlyPpwValues = comparisonPricing
    .map((pricing) => {
      if (!pricing.systemSize || pricing.systemSize <= 0) {
        return null
      }

      const solarOnlyPrice = pricing.totalContractPrice - batteryCost
      return solarOnlyPrice / pricing.systemSize
    })
    .filter((value): value is number => typeof value === `number`)

  if (solarOnlyPpwValues.length === 0) {
    return { min: null, max: null }
  }

  return {
    min: Math.min(...solarOnlyPpwValues),
    max: Math.max(...solarOnlyPpwValues),
  }
}

export function getGaugePositions(
  catalogMin: number,
  catalogMax: number,
  target: number,
): { barLeft: number; barWidth: number; targetPos: number } {
  const rangeMin = Math.min(catalogMin, target) - 0.2
  const rangeMax = Math.max(catalogMax, target) + 0.2
  const total = rangeMax - rangeMin
  if (total <= 0 || !isFinite(total)) {
    return { barLeft: 0, barWidth: 0, targetPos: 50 }
  }
  return {
    barLeft: ((catalogMin - rangeMin) / total) * 100,
    barWidth: ((catalogMax - catalogMin) / total) * 100,
    targetPos: ((target - rangeMin) / total) * 100,
  }
}

const FALLBACK_SAVE_ERROR_MESSAGE = `Unable to save your selections. Please try again.`

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === `object` && value !== null
}

const getMessageFromData = (data: unknown): string | null => {
  if (!isRecord(data)) {
    return null
  }

  const { message, error: dataError } = data
  if (typeof message === `string` && message.length > 0) {
    return message
  }

  if (Array.isArray(message) && message.length > 0) {
    return message.join(`; `)
  }

  if (typeof dataError === `string` && dataError.length > 0) {
    return dataError
  }

  return null
}

export const getFinancePlanErrorMessage = (error: unknown): string => {
  if (isRecord(error) && isRecord(error.response)) {
    const responseMessage = getMessageFromData(error.response.data)
    if (responseMessage) {
      return responseMessage
    }
  }

  if (error instanceof Error && error.message.length > 0) {
    return error.message
  }

  return FALLBACK_SAVE_ERROR_MESSAGE
}
